// fragmentCache — learner-side IDB cache of fragment metadata.
//
// Same offline-first paint strategy as messageCache: cold launch on a
// hill with no signal paints the last-known fragments instantly while
// the live Firestore listener catches up.
//
// What's stored: FragmentDoc metadata (photoUrls / videoUrl are strings,
// not blobs). Browser HTTP cache handles binary media; we don't double-
// cache images/videos in IDB.

import type { FragmentDoc, WithId } from "@goodbye/firebase";
import { Timestamp } from "firebase/firestore";
import { createAudienceCache } from "./audienceCache";

interface StoredFragment {
  id: string;
  createdAtMs: number;
  retractedAtMs: number | null;
  body: Omit<FragmentDoc, "createdAt" | "retractedAt">;
}

function toStored(f: WithId<FragmentDoc>): StoredFragment {
  const { id, createdAt, retractedAt, ...rest } = f;
  return {
    id,
    createdAtMs: createdAt instanceof Timestamp ? createdAt.toMillis() : 0,
    retractedAtMs:
      retractedAt instanceof Timestamp ? retractedAt.toMillis() : null,
    body: rest,
  };
}

function fromStored(s: StoredFragment): WithId<FragmentDoc> {
  return {
    ...s.body,
    id: s.id,
    createdAt: Timestamp.fromMillis(s.createdAtMs),
    retractedAt:
      s.retractedAtMs != null
        ? Timestamp.fromMillis(s.retractedAtMs)
        : undefined,
  } as WithId<FragmentDoc>;
}

const cache = createAudienceCache<WithId<FragmentDoc>, StoredFragment>({
  dbName: "goodbye-user-fragments",
  storeName: "fragments",
  encode: toStored,
  decode: fromStored,
});

export async function writeFragmentsCache(
  fragments: WithId<FragmentDoc>[],
): Promise<void> {
  await cache.write(fragments);
}

export async function readFragmentsCache(): Promise<WithId<FragmentDoc>[]> {
  return cache.read();
}

/** Called when a silent fragment_retracted push arrives. */
export async function deleteFragmentFromCache(
  fragmentId: string,
): Promise<void> {
  await cache.delete(fragmentId);
}
